import React, { useRef, useState, useEffect } from "react";
import {
  Container,
  Typography,
  Card,
  Grid,
  Box,
  Button,
  Paper,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Share as ShareIcon,
  LocalBar as DrinksIcon,
  Restaurant as FoodIcon,
  Fastfood as FastFoodIcon,
  BrunchDining as BrunchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WhatsApp as WhatsAppIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  EmojiFoodBeverage as CoffeeIcon,
  LocalPizza as PizzaIcon,
  Cake as DessertIcon,
  SetMeal as MainCourseIcon,
  SoupKitchen as SoupIcon,
  RiceBowl as RiceIcon,
} from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";

// Import local images
import slide1 from '../../src/assets/eleganted.PNG';
import slide2 from '../../src/assets/elegant.PNG';
import slide3 from '../../src/assets/bg.PNG';

// Category icons mapping based on your database categories
const categoryIcons = {
  "Local Main Course": <MainCourseIcon />,
  "Continental Main Course": <FoodIcon />,
  "Drinks ": <DrinksIcon />,
  "Shawarma": <FastFoodIcon />,
  "Pizza ": <PizzaIcon />,
  "Platters": <BrunchIcon />,
  "Charcoal Grill": <FoodIcon />,
  "Local Starters": <SoupIcon />,
  "Pepper Soup": <SoupIcon />,
  "Dessert": <DessertIcon />,
  "Burger": <FastFoodIcon />,
  "Continental Starters": <SoupIcon />,
  "Salad": <BrunchIcon />,
  "Pasta": <FoodIcon />,
};

// Subcategory icons mapping
const subcategoryIcons = {
  "Starters": <SoupIcon />,
  "Main Dishes": <MainCourseIcon />,
  "Desserts": <DessertIcon />,
  "Cold Drinks": <DrinksIcon />,
  "Hot Drinks": <CoffeeIcon />,
  "Pizza": <PizzaIcon />,
  "Pasta": <FoodIcon />,
  "Salads": <BrunchIcon />,
  "Rice": <RiceIcon />,
  "Snacks": <FastFoodIcon />,
};

// Carousel slides data with local images
const carouselSlides = [
  {
    id: 1,
    image: slide1,
    title: "Welcome to Green Velvet",
    subtitle: "Experience Culinary Excellence"
  },
  {
    id: 2,
    image: slide2,
    title: "Fine Dining Experience",
    subtitle: "Exceptional Food & Atmosphere"
  },
  {
    id: 3,
    image: slide3,
    title: "Crafted with Passion",
    subtitle: "Fresh Ingredients, Perfect Flavors"
  }
];

const MainContent = ({ menuData, qrMenuURL }) => {
  const qrRef = useRef();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processedMenuData, setProcessedMenuData] = useState([]);

  // Debug console log for incoming menu data
  console.debug("[MainContent] Received menu data:", menuData);
  console.debug("[MainContent] QR Menu URL:", qrMenuURL);

  // Process menu data to remove duplicate subcategory names
  const processMenuData = (data) => {
    if (!data || !Array.isArray(data)) {
      console.warn("[MainContent] Invalid menu data received:", data);
      return [];
    }

    console.debug("[MainContent] Processing menu data to remove duplicate subcategories...");

    const processed = data.map(category => {
      // Create a deep copy to avoid mutating original data
      const categoryCopy = {
        ...category,
        subcategories: category.subcategories ? [...category.subcategories] : [],
        items: category.items ? [...category.items] : []
      };

      // Filter subcategories that have the same name as the parent category
      const uniqueSubcategories = [];
      const itemsToPromote = [];

      categoryCopy.subcategories.forEach(subcategory => {
        // Normalize names for comparison (trim and convert to lowercase)
        const normalizedCategoryName = (categoryCopy.name || "").trim().toLowerCase();
        const normalizedSubcategoryName = (subcategory.name || "").trim().toLowerCase();
        
        // Check if subcategory name matches category name
        const isDuplicate = normalizedCategoryName === normalizedSubcategoryName;
        
        console.debug(`[MainContent] Category: "${categoryCopy.name}", Subcategory: "${subcategory.name}", IsDuplicate: ${isDuplicate}`);

        if (isDuplicate) {
          // If duplicate, collect items to promote to main category
          if (subcategory.items && subcategory.items.length > 0) {
            console.debug(`[MainContent] Promoting ${subcategory.items.length} items from duplicate subcategory "${subcategory.name}" to main category "${categoryCopy.name}"`);
            itemsToPromote.push(...subcategory.items);
          }
        } else {
          // Keep unique subcategories
          uniqueSubcategories.push(subcategory);
        }
      });

      // Promote collected items to main category
      if (itemsToPromote.length > 0) {
        categoryCopy.items = [...categoryCopy.items, ...itemsToPromote];
        console.debug(`[MainContent] Promoted ${itemsToPromote.length} items to main category "${categoryCopy.name}". Total items now: ${categoryCopy.items.length}`);
      }

      // Update subcategories with unique ones only
      categoryCopy.subcategories = uniqueSubcategories;

      // Remove any empty subcategories that have no items
      categoryCopy.subcategories = categoryCopy.subcategories.filter(sub => 
        sub.items && sub.items.length > 0
      );

      console.debug(`[MainContent] Category "${categoryCopy.name}" final state: ${categoryCopy.subcategories.length} subcategories, ${categoryCopy.items.length} direct items`);

      return categoryCopy;
    });

    // Filter out categories that have no content after processing
    const filteredProcessed = processed.filter(category => 
      (category.subcategories && category.subcategories.length > 0) || 
      (category.items && category.items.length > 0)
    );

    console.debug("[MainContent] Processed menu data:", filteredProcessed);
    return filteredProcessed;
  };

  // Sort categories by sort_order (lower number first), then by id as fallback
  const sortedMenuData = React.useMemo(() => {
    if (!processedMenuData || !Array.isArray(processedMenuData)) return [];
    const sorted = [...processedMenuData].sort((a, b) => {
      const orderA = a.sort_order !== null && a.sort_order !== undefined ? a.sort_order : Number.MAX_SAFE_INTEGER;
      const orderB = b.sort_order !== null && b.sort_order !== undefined ? b.sort_order : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return (a.id || 0) - (b.id || 0);
    });
    console.debug("[MainContent] Sorted menu categories:", sorted.map(c => ({ id: c.id, name: c.name, order: c.sort_order })));
    return sorted;
  }, [processedMenuData]);

  // Process menu data when it changes
  useEffect(() => {
    console.debug("[MainContent] Processing menu data on mount/update...");
    const processed = processMenuData(menuData);
    setProcessedMenuData(processed);
  }, [menuData]);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000);
    console.debug("[MainContent] Carousel auto-advance timer started");
    return () => {
      clearInterval(timer);
      console.debug("[MainContent] Carousel auto-advance timer cleared");
    };
  }, []);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      console.debug("[MainContent] Loading completed");
    }, 2500);
    return () => {
      clearTimeout(timer);
      console.debug("[MainContent] Loading timer cleared");
    };
  }, []);

  const handleDownloadQR = async () => {
    console.debug("[MainContent] Downloading QR code...");
    try {
      const canvas = await html2canvas(qrRef.current);
      const link = document.createElement("a");
      link.download = "green-velvet-menu-qr.png";
      link.href = canvas.toDataURL();
      link.click();
      console.debug("[MainContent] QR code downloaded successfully");
    } catch (error) {
      console.error("[MainContent] Error downloading QR code:", error);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    console.debug(`[MainContent] Toggling category ${categoryId}`);
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSubcategoryToggle = (subcategoryId) => {
    console.debug(`[MainContent] Toggling subcategory ${subcategoryId}`);
    setExpandedSubcategories(prev => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId]
    }));
  };

  const handleToggleAll = () => {
    console.debug(`[MainContent] Toggling all sections. Current state: allExpanded = ${allExpanded}`);
    if (allExpanded) {
      setExpandedCategories({});
      setExpandedSubcategories({});
      console.debug("[MainContent] Collapsed all sections");
    } else {
      const allExpandedState = {};
      const allSubExpandedState = {};
      
      sortedMenuData.forEach(category => {
        allExpandedState[category.id] = true;
        category.subcategories?.forEach(subcategory => {
          allSubExpandedState[subcategory.id] = true;
        });
      });
      
      setExpandedCategories(allExpandedState);
      setExpandedSubcategories(allSubExpandedState);
      console.debug("[MainContent] Expanded all sections", { 
        categories: Object.keys(allExpandedState).length, 
        subcategories: Object.keys(allSubExpandedState).length 
      });
    }
    setAllExpanded(!allExpanded);
  };

  const handleImageLoad = (subcategoryId) => {
    console.debug(`[MainContent] Image loaded for subcategory ${subcategoryId}`);
    setImageLoaded(prev => ({ ...prev, [subcategoryId]: true }));
  };

  const handlePlaceOrder = () => {
    console.debug("[MainContent] Placing order via WhatsApp...");
    const message = "Hello! I'd like to place an order from your menu.";
    const whatsappURL = `https://wa.me/2347068686839?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
    console.debug("[MainContent] WhatsApp link opened");
  };

  const handleExploreMenu = () => {
    console.debug("[MainContent] Scrolling to menu section...");
    const menuSection = document.getElementById('menu-section');
    if (menuSection) {
      menuSection.scrollIntoView({ behavior: 'smooth' });
      console.debug("[MainContent] Scrolled to menu section");
    } else {
      console.warn("[MainContent] Menu section not found");
    }
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => {
      const next = (prev + 1) % carouselSlides.length;
      console.debug(`[MainContent] Carousel slide changed: ${prev} -> ${next}`);
      return next;
    });
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => {
      const prevSlide = (prev - 1 + carouselSlides.length) % carouselSlides.length;
      console.debug(`[MainContent] Carousel slide changed: ${prev} -> ${prevSlide}`);
      return prevSlide;
    });
  };

  const formatPrice = (price) => {
    // Validate price input
    if (price === null || price === undefined) {
      console.warn("[MainContent] Invalid price value:", price);
      return '₦0.00';
    }
    
    let num;
    if (typeof price === "string") {
      num = parseFloat(price);
      if (isNaN(num)) {
        console.warn("[MainContent] Invalid price string:", price);
        return '₦0.00';
      }
    } else if (typeof price === "number") {
      num = price;
    } else {
      console.warn("[MainContent] Invalid price type:", typeof price, price);
      return '₦0.00';
    }
    
    // Check if price is zero or negative
    if (num <= 0) {
      console.debug(`[MainContent] Item has zero or negative price: ${num}`);
      return 'Call for price';
    }
    
    const formatted = `₦${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    return formatted;
  };

  // Get icon for subcategory
  const getSubcategoryIcon = (subcategoryName) => {
    if (!subcategoryName) {
      console.warn("[MainContent] Empty subcategory name provided to getSubcategoryIcon");
      return <FoodIcon />;
    }
    return subcategoryIcons[subcategoryName] || <FoodIcon />;
  };

  // Validate and sanitize item description
  const sanitizeDescription = (description) => {
    if (!description) return null;
    // Basic XSS prevention by escaping HTML
    const tempDiv = document.createElement('div');
    tempDiv.textContent = description;
    return tempDiv.innerHTML;
  };

  // Loading Component
  if (loading) {
    console.debug("[MainContent] Rendering loading screen...");
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `
            linear-gradient(
              135deg,
              rgba(10, 10, 10, 0.98) 0%,
              rgba(20, 20, 20, 0.95) 50%,
              rgba(10, 10, 10, 0.98) 100%
            )
          `,
          zIndex: 9999,
          color: "white",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Animated Circular Progress */}
          <CircularProgress
            size={80}
            thickness={4}
            sx={{
              color: "#4caf50",
              animation: "pulse 2s infinite ease-in-out",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
                "50%": {
                  transform: "scale(1.1)",
                  opacity: 0.7,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          />
          
          {/* Outer rotating ring */}
          <CircularProgress
            size={100}
            thickness={1}
            variant="determinate"
            value={75}
            sx={{
              position: "absolute",
              color: "rgba(76, 175, 80, 0.3)",
              animation: "rotate 3s linear infinite",
              "@keyframes rotate": {
                "0%": {
                  transform: "rotate(0deg)",
                },
                "100%": {
                  transform: "rotate(360deg)",
                },
              },
            }}
          />
          
          {/* Inner rotating ring */}
          <CircularProgress
            size={120}
            thickness={1}
            variant="determinate"
            value={50}
            sx={{
              position: "absolute",
              color: "rgba(76, 175, 80, 0.2)",
              animation: "rotate 4s linear infinite reverse",
              "@keyframes rotate": {
                "0%": {
                  transform: "rotate(0deg)",
                },
                "100%": {
                  transform: "rotate(360deg)",
                },
              },
            }}
          />
        </Box>
        
        {/* Loading Text */}
        <Typography
          variant="h4"
          sx={{
            mt: 4,
            fontWeight: "bold",
            background: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            textAlign: "center",
            animation: "fadeInOut 2s infinite ease-in-out",
            "@keyframes fadeInOut": {
              "0%, 100%": {
                opacity: 0.7,
                transform: "translateY(0px)",
              },
              "50%": {
                opacity: 1,
                transform: "translateY(-5px)",
              },
            },
          }}
        >
          Green Velvet
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            color: "rgba(255, 255, 255, 0.7)",
            textAlign: "center",
            animation: "fadeIn 1.5s ease-in-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 0.7,
              },
            },
          }}
        >
          Preparing your exquisite dining experience...
        </Typography>
      </Box>
    );
  }

  if (!sortedMenuData || sortedMenuData.length === 0) {
    console.warn("[MainContent] No menu categories available to display");
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography variant="h6">No menu categories available</Typography>
      </Box>
    );
  }

  console.debug(`[MainContent] Rendering main content with ${sortedMenuData.length} categories`);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background: `
          linear-gradient(
            rgba(10, 10, 10, 0.85),
            rgba(20, 20, 20, 0.9)
          )
        `,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 20% 80%,
              rgba(120, 119, 198, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 119, 198, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 40% 40%,
              rgba(120, 219, 255, 0.05) 0%,
              transparent 50%
            )
          `,
          zIndex: 0,
        },
      }}
    >
      {/* Carousel Section */}
      <Box
        sx={{
          position: "relative",
          height: "70vh",
          minHeight: "500px",
          overflow: "hidden",
          mb: 4,
        }}
      >
        {carouselSlides.map((slide, index) => (
          <Box
            key={slide.id}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: index === currentSlide ? 1 : 0,
              transform: `translateX(${(index - currentSlide) * 100}%)`,
              transition: "all 0.8s ease-in-out",
              background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${slide.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
              <Box sx={{ textAlign: "center", color: "white" }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: "bold",
                    mb: 3,
                    fontSize: { xs: "2.5rem", md: "4rem" },
                    background: "linear-gradient(135deg, #fff 0%, #ccc 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  {slide.title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    fontSize: { xs: "1.2rem", md: "2rem" },
                    color: "#e0e0e0",
                  }}
                >
                  {slide.subtitle}
                </Typography>
                <Box sx={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={handleExploreMenu}
                    sx={{
                      background: `
                        linear-gradient(
                          135deg,
                          rgba(76, 175, 80, 0.9) 0%,
                          rgba(56, 142, 60, 0.9) 100%
                        )
                      `,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      "&:hover": {
                        background: `
                          linear-gradient(
                            135deg,
                            rgba(96, 195, 100, 0.9) 0%,
                            rgba(76, 162, 80, 0.9) 100%
                          )
                        `,
                        transform: "translateY(-3px)",
                        boxShadow: "0 15px 30px rgba(76, 175, 80, 0.4)",
                      },
                      px: 5,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                      boxShadow: "0 10px 25px rgba(76, 175, 80, 0.3)",
                    }}
                  >
                    Explore Menu
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handlePlaceOrder}
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      background: `
                        linear-gradient(
                          135deg,
                          rgba(255, 255, 255, 0.9) 0%,
                          rgba(200, 200, 200, 0.9) 100%
                        )
                      `,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      color: "#1a1a1a",
                      "&:hover": {
                        background: `
                          linear-gradient(
                            135deg,
                            rgba(255, 255, 255, 1) 0%,
                            rgba(230, 230, 230, 1) 100%
                        )`,
                        transform: "translateY(-3px)",
                        boxShadow: "0 15px 30px rgba(255, 255, 255, 0.3)",
                      },
                      px: 5,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                      boxShadow: "0 10px 25px rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Place Order Now
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        ))}
        
        {/* Carousel Navigation */}
        <IconButton
          onClick={handlePrevSlide}
          sx={{
            position: "absolute",
            left: 20,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            backgroundColor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
            zIndex: 3,
          }}
        >
          <NavigateBeforeIcon />
        </IconButton>
        
        <IconButton
          onClick={handleNextSlide}
          sx={{
            position: "absolute",
            right: 20,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            backgroundColor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
            zIndex: 3,
          }}
        >
          <NavigateNextIcon />
        </IconButton>

        {/* Carousel Indicators */}
        <Box
          sx={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
            zIndex: 3,
          }}
        >
          {carouselSlides.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: index === currentSlide ? "white" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "white",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Container maxWidth={false} sx={{ position: "relative", zIndex: 1, py: 4, px: { xs: 2, sm: 3, md: 4 } }} id="menu-section">
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 4,
            background: `
              linear-gradient(
                135deg,
                rgba(30, 30, 30, 0.95) 0%,
                rgba(20, 20, 20, 0.98) 50%,
                rgba(30, 30, 30, 0.95) 100%
              )
            `,
            color: "white",
            borderRadius: 3,
            textAlign: "center",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                linear-gradient(
                  45deg,
                  transparent 0%,
                  rgba(76, 175, 80, 0.05) 50%,
                  transparent 100%
                )
              `,
              zIndex: 0,
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: "bold", 
                mb: 2,
                background: "linear-gradient(135deg, #fff 0%, #bbb 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                textShadow: "0 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              Our Menu
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.8,
                color: "#e0e0e0",
                fontWeight: 300,
                letterSpacing: "0.5px"
              }}
            >
              Discover our exquisite selection of culinary delights
            </Typography>
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={handleToggleAll}
            sx={{
              borderColor: "rgba(255, 255, 255, 0.3)",
              color: "white",
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderColor: "rgba(255, 255, 255, 0.5)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
              },
              transition: "all 0.3s ease",
              borderRadius: 2,
              px: 3,
              py: 1.2,
              borderWidth: "2px",
              fontWeight: 600,
            }}
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
          
          <Button
            variant="contained"
            onClick={handlePlaceOrder}
            startIcon={<WhatsAppIcon />}
            sx={{
              background: `
                linear-gradient(
                  135deg,
                  rgba(255, 255, 255, 0.9) 0%,
                  rgba(200, 200, 200, 0.9) 100%
                )
              `,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              color: "#1a1a1a",
              "&:hover": {
                background: `
                  linear-gradient(
                    135deg,
                    rgba(255, 255, 255, 1) 0%,
                    rgba(230, 230, 230, 1) 100%
                )`,
                transform: "translateY(-2px)",
                boxShadow: "0 12px 30px rgba(255, 255, 255, 0.3)",
              },
              px: 4,
              py: 1.2,
              borderRadius: 2,
              boxShadow: "0 8px 20px rgba(255, 255, 255, 0.2)",
              transition: "all 0.3s ease",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            Place Order Now
          </Button>
        </Box>

        {/* Vertical Category Sections */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            {sortedMenuData.map((category, index) => (
              <Paper
                key={category.id || index}
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  overflow: "hidden",
                  background: "rgba(20, 20, 20, 0.7)",
                  backdropFilter: "blur(20px)",
                  transition: "all 0.4s ease",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                  },
                }}
              >
                {/* Category Header - Clickable */}
                <Box
                  onClick={() => handleCategoryToggle(category.id || index)}
                  sx={{
                    p: 3,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: `
                      linear-gradient(
                        135deg,
                        rgba(50, 50, 50, 0.95) 0%,
                        rgba(40, 40, 40, 0.98) 100%
                      )
                    `,
                    color: "white",
                    transition: "all 0.3s ease",
                    position: "relative",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                    "&:hover": {
                      background: `
                        linear-gradient(
                          135deg,
                          rgba(60, 60, 60, 0.95) 0%,
                          rgba(50, 50, 50, 0.98) 100%
                        )
                      `,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: "20%",
                      right: "20%",
                      height: "2px",
                      background: "linear-gradient(90deg, transparent, #4caf50, transparent)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                        borderRadius: "12px",
                        width: 56,
                        height: 56,
                        mr: 2,
                        border: "1px solid rgba(76, 175, 80, 0.3)",
                      }}
                    >
                      {React.cloneElement(categoryIcons[category.name] || <FoodIcon />, {
                        sx: { color: "#4caf50", fontSize: 28 }
                      })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold",
                          background: "linear-gradient(135deg, #fff 0%, #ccc 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          color: "transparent"
                        }}
                      >
                        {category.name}
                      </Typography>
                      <Chip
                        label={`${(category.subcategories?.length || 0) + (category.items?.length > 0 ? 1 : 0)} sections`}
                        size="small"
                        sx={{
                          mt: 1,
                          backgroundColor: "rgba(76, 175, 80, 0.2)",
                          color: "#4caf50",
                          fontWeight: "bold",
                          border: "1px solid rgba(76, 175, 80, 0.3)",
                          backdropFilter: "blur(10px)",
                        }}
                      />
                    </Box>
                  </Box>
                  <IconButton
                    sx={{ 
                      color: "#4caf50", 
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      border: "1px solid rgba(76, 175, 80, 0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                      }
                    }}
                  >
                    {expandedCategories[category.id || index] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>

                {/* Category Content - Collapsible */}
                <Collapse in={expandedCategories[category.id || index]}>
                  <Box sx={{ p: 3 }}>
                    {/* Display unique subcategories (non-duplicate) */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <>
                        {category.subcategories.map((subcategory, subIndex) => (
                          <Card
                            key={subcategory.id || subIndex}
                            elevation={0}
                            sx={{
                              mb: 3,
                              borderRadius: 2,
                              overflow: "hidden",
                              background: "rgba(30, 30, 30, 0.6)",
                              border: "1px solid rgba(255, 255, 255, 0.08)",
                              backdropFilter: "blur(15px)",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
                                border: "1px solid rgba(255, 255, 255, 0.12)",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {/* Subcategory Header with Icon */}
                            <Box
                              onClick={() => handleSubcategoryToggle(subcategory.id || subIndex)}
                              sx={{
                                cursor: "pointer",
                                position: "relative",
                                minHeight: 120,
                                background: `
                                  linear-gradient(
                                    135deg,
                                    rgba(40, 40, 40, 0.95) 0%,
                                    rgba(30, 30, 30, 0.98) 100%
                                  )
                                `,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 3,
                                transition: "all 0.4s ease",
                                "&:hover": {
                                  background: `
                                    linear-gradient(
                                      135deg,
                                      rgba(50, 50, 50, 0.95) 0%,
                                      rgba(40, 40, 40, 0.98) 100%
                                    )
                                  `,
                                },
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(76, 175, 80, 0.15)",
                                    borderRadius: "10px",
                                    width: 48,
                                    height: 48,
                                    mr: 2,
                                    border: "1px solid rgba(76, 175, 80, 0.25)",
                                  }}
                                >
                                  {React.cloneElement(getSubcategoryIcon(subcategory.name), {
                                    sx: { color: "#4caf50", fontSize: 24 }
                                  })}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: "bold", 
                                      color: "white",
                                      fontSize: "1.1rem"
                                    }}
                                  >
                                    {subcategory.name}
                                  </Typography>
                                  <Chip
                                    label={`${subcategory.items?.length || 0} items`}
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      backgroundColor: "rgba(255,255,255,0.15)",
                                      color: "white",
                                      fontWeight: "bold",
                                      backdropFilter: "blur(10px)",
                                      border: "1px solid rgba(255,255,255,0.2)",
                                    }}
                                  />
                                </Box>
                              </Box>
                              <IconButton
                                sx={{ 
                                  color: "white", 
                                  backgroundColor: "rgba(255,255,255,0.15)",
                                  border: "1px solid rgba(255,255,255,0.2)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.25)",
                                  }
                                }}
                              >
                                {expandedSubcategories[subcategory.id || subIndex] ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            </Box>

                            {/* Subcategory Items - Collapsible */}
                            <Collapse in={expandedSubcategories[subcategory.id || subIndex]}>
                              <Box sx={{ p: 0 }}>
                                <List sx={{ width: "100%" }}>
                                  {subcategory.items && subcategory.items.length > 0 ? (
                                    subcategory.items.map((item, itemIndex) => (
                                      <React.Fragment key={item.id || itemIndex}>
                                        <ListItem 
                                          sx={{ 
                                            py: 2.5,
                                            px: 3,
                                            background: "transparent",
                                            "&:hover": {
                                              background: "rgba(255, 255, 255, 0.03)",
                                            },
                                            transition: "all 0.2s ease",
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Box sx={{ 
                                                display: "flex", 
                                                justifyContent: "space-between", 
                                                alignItems: "center",
                                                width: "100%",
                                                gap: 2
                                              }}>
                                                <Typography 
                                                  variant="body1" 
                                                  sx={{ 
                                                    fontWeight: "600",
                                                    color: "#e2e8f0",
                                                    lineHeight: 1.4,
                                                    flex: 1,
                                                    textAlign: "left",
                                                    minWidth: 0
                                                  }}
                                                >
                                                  {item.name}
                                                </Typography>
                                                <Typography 
                                                  variant="body1" 
                                                  sx={{ 
                                                    fontWeight: "bold",
                                                    color: "#4caf50",
                                                    minWidth: "100px",
                                                    textAlign: "right",
                                                    fontSize: "1.1rem",
                                                    flexShrink: 0
                                                  }}
                                                >
                                                  {formatPrice(item.price)}
                                                </Typography>
                                              </Box>
                                            }
                                            secondary={
                                              item.description && (
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    mt: 1, 
                                                    color: "#a0aec0",
                                                    lineHeight: 1.5,
                                                    fontStyle: "italic",
                                                    textAlign: "left"
                                                  }}
                                                >
                                                  {sanitizeDescription(item.description)}
                                                </Typography>
                                              )
                                            }
                                          />
                                        </ListItem>
                                        {itemIndex < subcategory.items.length - 1 && (
                                          <Divider 
                                            sx={{ 
                                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                                              mx: 3
                                            }} 
                                          />
                                        )}
                                      </React.Fragment>
                                    ))
                                  ) : (
                                    <Box sx={{ p: 3, textAlign: "center" }}>
                                      <Typography variant="body2" sx={{ color: "#718096" }}>
                                        No items available in this subcategory
                                      </Typography>
                                    </Box>
                                  )}
                                </List>
                              </Box>
                            </Collapse>
                          </Card>
                        ))}
                      </>
                    )}

                    {/* Display direct category items (promoted from duplicate subcategories) */}
                    {category.items && category.items.length > 0 && (
                      <Card
                        elevation={0}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          overflow: "hidden",
                          background: "rgba(30, 30, 30, 0.6)",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          backdropFilter: "blur(15px)",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
                            border: "1px solid rgba(255, 255, 255, 0.12)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                      
                        <List sx={{ width: "100%" }}>
                          {category.items.map((item, itemIndex) => (
                            <React.Fragment key={item.id || itemIndex}>
                              <ListItem 
                                sx={{ 
                                  py: 2.5,
                                  px: 3,
                                  background: "transparent",
                                  "&:hover": {
                                    background: "rgba(255, 255, 255, 0.03)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ 
                                      display: "flex", 
                                      justifyContent: "space-between", 
                                      alignItems: "center",
                                      width: "100%",
                                      gap: 2
                                    }}>
                                      <Typography 
                                        variant="body1" 
                                        sx={{ 
                                          fontWeight: "600",
                                          color: "#e2e8f0",
                                          lineHeight: 1.4,
                                          flex: 1,
                                          textAlign: "left",
                                          minWidth: 0
                                        }}
                                      >
                                        {item.name}
                                      </Typography>
                                      <Typography 
                                        variant="body1" 
                                        sx={{ 
                                          fontWeight: "bold",
                                          color: "#4caf50",
                                          minWidth: "100px",
                                          textAlign: "right",
                                          fontSize: "1.1rem",
                                          flexShrink: 0
                                        }}
                                      >
                                        {formatPrice(item.price)}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    item.description && (
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          mt: 1, 
                                          color: "#a0aec0",
                                          lineHeight: 1.5,
                                          fontStyle: "italic",
                                          textAlign: "left"
                                        }}
                                      >
                                        {sanitizeDescription(item.description)}
                                      </Typography>
                                    )
                                  }
                                />
                              </ListItem>
                              {itemIndex < category.items.length - 1 && (
                                <Divider 
                                  sx={{ 
                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                    mx: 3
                                  }} 
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Card>
                    )}
                    
                    {(!category.subcategories || category.subcategories.length === 0) && 
                     (!category.items || category.items.length === 0) && (
                      <Box sx={{ textAlign: "center", p: 4 }}>
                        <Typography variant="body2" sx={{ color: "#718096" }}>
                          No items available in this category
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Grid>

          {/* QR Code Sidebar */}
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 20,
                p: 4,
                textAlign: "center",
                borderRadius: 3,
                background: `
                  linear-gradient(
                    135deg,
                    rgba(30, 30, 30, 0.8) 0%,
                    rgba(20, 20, 20, 0.9) 100%
                )`,
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(
                      circle at 30% 70%,
                      rgba(76, 175, 80, 0.1) 0%,
                      transparent 50%
                    )
                  `,
                  zIndex: 0,
                },
              }}
            >
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: "bold", 
                    background: "linear-gradient(135deg, #fff 0%, #ccc 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    mb: 2
                  }}
                >
                  Digital Menu
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    color: "#a0aec0", 
                    mb: 3,
                    lineHeight: 1.6
                  }}
                >
                  Scan the QR code to view our menu on your mobile device
                </Typography>
                
                <Box
                  ref={qrRef}
                  sx={{
                    p: 3,
                    border: "2px solid rgba(76, 175, 80, 0.3)",
                    display: "inline-block",
                    mb: 3,
                    background: "rgba(10, 10, 10, 0.6)",
                    borderRadius: 2,
                    boxShadow: "0 12px 30px rgba(76, 175, 80, 0.2)",
                    position: "relative",
                    backdropFilter: "blur(10px)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -4,
                      left: "15%",
                      right: "15%",
                      height: "3px",
                      background: "linear-gradient(90deg, transparent, #4caf50, transparent)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  <QRCodeCanvas
                    value={qrMenuURL}
                    size={200}
                    fgColor="#4caf50"
                    bgColor="transparent"
                    level="H"
                    includeMargin={false}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: `
                      linear-gradient(
                        135deg,
                        rgba(76, 175, 80, 0.9) 0%,
                        rgba(56, 142, 60, 0.9) 100%
                      )
                    `,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      background: `
                        linear-gradient(
                          135deg,
                          rgba(96, 195, 100, 0.9) 0%,
                          rgba(76, 162, 80, 0.9) 100%
                        )
                      `,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 25px rgba(76, 175, 80, 0.4)",
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: "0 8px 20px rgba(76, 175, 80, 0.3)",
                    transition: "all 0.3s ease",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                  startIcon={<ShareIcon />}
                  onClick={handleDownloadQR}
                >
                  Download QR Code
                </Button>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: "block", 
                    mt: 2, 
                    color: "#718096",
                    fontStyle: "italic"
                  }}
                >
                  Perfect for sharing with friends and family
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: "#718096", opacity: 0.7 }}>
            © {new Date().getFullYear()} Green Velvet. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default MainContent;